/**
 * LayerManager: the render layers that govern depth occlusion between the
 * gaussian scene, the occlusion proxy, the character, and the UI.
 *
 * The gaussian splat is NOT moved off its default World layer (LAYERID_WORLD) —
 * reassigning GSplatComponent.layers before the entity is parented leaves it in
 * no layer (see loadGaussianScene). Instead the camera includes LAYERID_WORLD.
 *
 * Six custom layers are added after the defaults. The draw order is:
 *   World(default) -> Occlusion -> World(custom) -> AvatarDepth -> AvatarOpaque
 *   -> AvatarTransparent
 *   - The splat draws first (visible scene, with its own depth).
 *   - Occlusion (collision.glb, colorWrite off, depthWrite on) reinforces depth
 *     at wall surfaces; rendered after the splat with LEQUAL depth test so it
 *     doesn't punch holes where the proxy pokes through the true surface.
 *   - Custom World props and mesh markers are depth-tested against the above.
 *     The custom World transparent pass is required for opacity < 1 marker
 *     panels while keeping the existing occlusion behavior.
 *   - The custom World layer also holds the remote players, so they are
 *     occluded by walls like any other prop.
 *   - AvatarDepth clears the scene depth, then re-writes the depth of
 *     everything allowed to occlude the local avatar: depth-only copies of
 *     each remote player's real avatar meshes (AvatarController.
 *     enableDepthOccluder), GLB marker occluder twins (markerFactory.
 *     attachAvatarDepthTwin), and the scene occluder itself (loadOcclusionMesh
 *     also draws on this layer). AvatarOpaque draws the local avatar against
 *     that depth, so the local player is occluded by remote players' actual
 *     silhouettes, by placed GLB props, and by scene walls alike (2026-09-28
 *     遮挡一致性；清深度的意义只剩「让高斯泼溅自身的不参与深度」——它本就
 *     不写深度，清不清无差，保留是为层语义完整)。
 * Overlay (transparent, last) holds editor gizmos and billboard-like helper
 * visuals that intentionally render above the scene.
 *
 * The directional light is pointed at the Avatar + World + Overlay layers so
 * the character and markers are lit (the splat is unlit).
 */

import { Application, Layer, LAYERID_SKYBOX, LAYERID_WORLD } from 'playcanvas';

class LayerManager {
    readonly avatarDepth: Layer;
    readonly avatarOpaque: Layer;
    readonly avatarTransparent: Layer;
    readonly occlusion: Layer;
    readonly world: Layer;
    readonly overlay: Layer;

    constructor(app: Application) {
        this.avatarDepth = new Layer({ name: 'AvatarDepth', clearDepthBuffer: true });
        this.avatarOpaque = new Layer({ name: 'AvatarOpaque' });
        this.avatarTransparent = new Layer({ name: 'AvatarTransparent' });
        this.occlusion = new Layer({ name: 'Occlusion' });
        this.world = new Layer({ name: 'World' });
        this.overlay = new Layer({ name: 'Overlay' });

        const comp = app.scene.layers;
        comp.pushOpaque(this.occlusion);
        comp.pushOpaque(this.world);
        comp.pushTransparent(this.world);
        // Clear the scene depth, then re-write the depth of everything allowed
        // to occlude the local avatar below: remote players' true meshes, GLB
        // marker twins, and the scene occluder (see file header).
        comp.pushOpaque(this.avatarDepth);
        comp.pushOpaque(this.avatarOpaque);
        comp.pushTransparent(this.avatarTransparent);
        // Transparent: markers / UI, drawn last.
        comp.pushTransparent(this.overlay);
    }

    /** Layer ids the camera must render: default Skybox + World (gsplat) + our custom layers. */
    get allIds(): number[] {
        return [LAYERID_SKYBOX, LAYERID_WORLD, this.occlusion.id, this.world.id, this.avatarDepth.id, this.avatarOpaque.id, this.avatarTransparent.id, this.overlay.id];
    }
}

// Module singleton — initialized once in createApp, read everywhere else
// (loadOcclusionMesh, AvatarController, markerFactory, ...).
let _instance: LayerManager | null = null;

/** Create the global layer set. Called once from createApp. */
function initLayers(app: Application): LayerManager {
    if (!_instance) _instance = new LayerManager(app);
    return _instance;
}

/** Access the global layer set (null before createApp runs). */
function getLayers(): LayerManager | null {
    return _instance;
}

/** Convenience: overlay layer id array for markers/gizmos (empty pre-init). */
function overlayIds(): number[] {
    const l = _instance;
    return l ? [l.overlay.id] : [];
}

/** Convenience: world layer id array for character/props (empty pre-init). */
function worldIds(): number[] {
    const l = _instance;
    return l ? [l.world.id] : [];
}

/** Convenience: avatar layer id array for the local third-person model. */
function avatarIds(): number[] {
    const l = _instance;
    return l ? [l.avatarOpaque.id, l.avatarTransparent.id] : [];
}

export { LayerManager, initLayers, getLayers, overlayIds, worldIds, avatarIds };
